import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { corporateTheme, corporateLabels } from '../themes/corporate';

const StrategyContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.textPrimary};
  text-align: center;
  margin-bottom: 3rem;
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: ${corporateTheme.fontFamily};
`;

const StrategyGrid = styled.div`
  display: grid;
  /* 4 cards: 2x2 layout for better balance */
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  margin-bottom: 4rem;
  
  @media (max-width: 1200px) {
    /* Switch to auto-fit for medium screens */
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  }
`;

const StrategyCard = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(10px);
  border-radius: ${corporateTheme.borderRadius.xl};
  padding: 3rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.color || corporateTheme.primaryGradient};
  }
`;

const StrategyIcon = styled.div`
  font-size: 0.9rem;
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.background};
  background: ${corporateTheme.primaryGradient};
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin: 0 auto 1.5rem;
  font-family: ${corporateTheme.fontFamily};
  letter-spacing: 0.5px;
  line-height: 1;
`;

const StrategyTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  color: ${corporateTheme.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  font-family: ${corporateTheme.fontFamily};
`;

const StrategyDescription = styled.p`
  color: ${corporateTheme.textSecondary};
  line-height: 1.6;
  font-size: 1rem;
  margin-bottom: 2rem;
  font-family: ${corporateTheme.fontFamily};
`;

const StrategyPoints = styled.ul`
  list-style: none;
  padding: 0;
`;

const StrategyPoint = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  font-size: 0.95rem;
  color: ${corporateTheme.textSecondary};
  font-family: ${corporateTheme.fontFamily};
  
  &:before {
    content: "✓";
    color: ${corporateTheme.primary};
    font-weight: bold;
    margin-right: 0.8rem;
    margin-top: 0.1rem;
  }
`;

const ContentSection = styled(motion.div)`
  background: rgba(37, 99, 235, 0.05);
  backdrop-filter: blur(10px);
  border-radius: ${corporateTheme.borderRadius.xl};
  padding: 3rem;
  border: 1px solid rgba(37, 99, 235, 0.15);
  box-shadow: ${corporateTheme.cardShadow};
  margin-top: 4rem;
`;

const ContentTitle = styled.h3`
  font-size: 2rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  color: ${corporateTheme.primary};
  margin-bottom: 2rem;
  text-align: center;
  font-family: ${corporateTheme.fontFamily};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContentHighlight = styled.div`
  background: ${corporateTheme.cardBackground};
  border-radius: ${corporateTheme.borderRadius.lg};
  padding: 2rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
`;

const HighlightTitle = styled.h4`
  font-size: 1.3rem;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 1rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  font-family: ${corporateTheme.fontFamily};
`;

const HighlightText = styled.p`
  color: ${corporateTheme.textSecondary};
  line-height: 1.6;
  font-size: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const Strategy = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const strategies = [
    {
      icon: corporateLabels.strategy,
      title: "Advertiser Acquisition",
      description: "Multi-channel approach to attract high-quality advertisers across Eastern Asian markets.",
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.primary} 0%, ${corporateTheme.primary} 100%)`,
      points: [
        "Direct sales & business development teams",
        "Industry events & conference participation",
        "Targeted LinkedIn and Google Ads campaigns",
        "Strategic partnerships with e-commerce platforms",
        "Public relations and media outreach"
      ]
    },
    {
      icon: corporateLabels.execution,
      title: "Affiliate Recruitment",
      description: "Comprehensive strategy to build a network of high-performing affiliates and publishers.",
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.secondary} 0%, ${corporateTheme.success} 100%)`,
      points: [
        "Proactive outreach to quality affiliates",
        "Targeted campaigns on affiliate platforms",
        "Content marketing and SEO optimization",
        "Attractive commission structures",
        "Influencer relations and partnerships"
      ]
    },
    {
      icon: corporateLabels.content,
      title: "Content Strategy",
      description: "€5M dedicated investment in building high-value, authoritative content websites.",
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.tertiary} 0%, ${corporateTheme.error} 100%)`,
      points: [
        "Native-speaking content creation teams",
        "Niche website development and optimization",
        "Advanced SEO and technical optimization",
        "Digital PR and link building campaigns",
        "Social media and community building"
      ]
    },
    {
      icon: corporateLabels.analysis,
      title: "Localization",
      description: "Deep market understanding and cultural adaptation for each target region.",
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.quaternary} 0%, ${corporateTheme.warning} 100%)`,
      points: [
        "Multi-language platform and content support",
        "Local payment method integrations",
        "Cultural sensitivity in marketing approaches",
        "Local teams for authentic communication",
        "Regional compliance and legal adherence"
      ]
    }
  ];

  return (
    <StrategyContainer ref={ref}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <Title variants={itemVariants}>
          Marketing & Strategy
        </Title>

        <StrategyGrid>
          {strategies.map((strategy, index) => (
            <StrategyCard
              key={index}
              variants={itemVariants}
              color={strategy.color}
              whileHover={{ scale: 1.02, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <StrategyIcon>{strategy.icon}</StrategyIcon>
              <StrategyTitle>{strategy.title}</StrategyTitle>
              <StrategyDescription>{strategy.description}</StrategyDescription>
              <StrategyPoints>
                {strategy.points.map((point, pointIndex) => (
                  <StrategyPoint key={pointIndex}>{point}</StrategyPoint>
                ))}
              </StrategyPoints>
            </StrategyCard>
          ))}
        </StrategyGrid>

        <ContentSection variants={itemVariants}>
          <ContentTitle>Content Marketing Strategy - €5M Investment</ContentTitle>
          <ContentGrid>
            <ContentHighlight>
              <HighlightTitle>Website Value Building</HighlightTitle>
              <HighlightText>
                Our unique approach involves creating a network of high-authority, niche-specific 
                websites that generate organic traffic and provide valuable content to users. 
                This strategy differentiates us from traditional affiliate networks by creating 
                our own traffic sources rather than just facilitating external partnerships.
              </HighlightText>
            </ContentHighlight>
            
            <ContentHighlight>
              <HighlightTitle>Competitive Advantage</HighlightTitle>
              <HighlightText>
                By investing heavily in proprietary content creation, we provide advertisers 
                with high-quality, pre-qualified traffic while building sustainable revenue 
                streams. Our owned media properties serve as a significant differentiator 
                in the competitive affiliate marketing landscape.
              </HighlightText>
            </ContentHighlight>
          </ContentGrid>
        </ContentSection>
      </motion.div>
    </StrategyContainer>
  );
};

export default Strategy; 